'use client';

import { useId, useState } from 'react';
import { cn } from '@cobrastyle/ui';

export interface AccordionSection {
  /** Stable key; also seeds the id pair that ties the button to its panel. */
  id: string;
  title: string;
  content: React.ReactNode;
}

interface ProductAccordionProps {
  sections: AccordionSection[];
  /**
   * Index of the section open on first render, or null for all closed.
   * Defaults to the first, so the description is readable without a click.
   */
  defaultOpen?: number | null;
  className?: string;
}

/**
 * The stacked sections under the gallery — Produktbeskrivning, Specifikationer,
 * Leverans & licens.
 *
 * Replaces the tabbed panel this page used to carry. One section is open at a
 * time, matching the canvas and the filter sidebar's `−`/`+` idiom, and
 * clicking the open one closes it.
 */
export function ProductAccordion({
  sections,
  defaultOpen = 0,
  className,
}: ProductAccordionProps) {
  const [openIndex, setOpenIndex] = useState<number | null>(defaultOpen);
  const baseId = useId();

  if (sections.length === 0) return null;

  return (
    <div className={cn('flex flex-col', className)}>
      {sections.map((section, index) => {
        const isOpen = openIndex === index;
        const panelId = `${baseId}-${section.id}-panel`;
        const buttonId = `${baseId}-${section.id}-button`;

        return (
          <div key={section.id} className="flex flex-col border-t border-foreground/[0.12]">
            <h2>
              <button
                type="button"
                id={buttonId}
                aria-expanded={isOpen}
                aria-controls={panelId}
                onClick={() => setOpenIndex(isOpen ? null : index)}
                className="flex w-full items-center justify-between px-0.5 py-5 text-left text-foreground transition-colors hover:text-accent-700"
              >
                <span className="font-heading text-[19px] font-semibold">{section.title}</span>
                <span aria-hidden="true" className="text-[22px] font-light leading-none text-accent-700">
                  {isOpen ? '−' : '+'}
                </span>
              </button>
            </h2>

            {isOpen && (
              <div id={panelId} role="region" aria-labelledby={buttonId} className="px-0.5 pb-6">
                {section.content}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}
